import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';

function statusBadge(s) {
  if (s === 'Accepted') return 'bg-success';
  if (s === 'Expired' || s === 'Declined') return 'bg-secondary';
  return 'bg-warning text-dark';
}

function fmt(ts) {
  return ts ? new Date(ts).toLocaleString() : '—';
}

export default function AffiliateBroadcastsPage() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [myJobs, setMyJobs] = useState([]);
  const [sofJob, setSofJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [offerMsg, setOfferMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    const [b, o, j] = await Promise.all([
      api.listBroadcasts(),
      api.getBroadcastOffers(),
      api.getMyBroadcastJobs(),
    ]);
    setBroadcasts(b || []);
    setOffers(o || []);
    setMyJobs(j || []);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        await load();
      } catch (err) {
        setLoadError(err.message || 'Failed to load broadcasts.');
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  const respond = async (offer, accept) => {
    setBusyId(offer.targetId);
    setOfferMsg(null);
    try {
      if (accept) {
        const res = await api.acceptOffer(offer.targetId);
        setOfferMsg({
          type: 'success',
          text: `Offer ${offer.bookingRef} ${res.responseStatus} — dispatcher notified.`,
        });
      } else {
        await api.declineOffer(offer.targetId);
        setOfferMsg({ type: 'secondary', text: `Offer ${offer.bookingRef} declined.` });
      }
      await load();
    } catch (err) {
      setOfferMsg({ type: 'danger', text: err.message || 'Failed to respond to the offer.' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="container-fluid py-4" data-testid="broadcasts-page">
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm">
            <div>
              <h3 className="text-primary mb-0">
                <i className="fas fa-satellite-dish me-2" aria-hidden="true" />
                Affiliate Job Broadcasts
              </h3>
              <p className="text-muted mb-0 small">
                Capacity broadcasts dispatched to affiliates, and your open subcontracting offers.
              </p>
            </div>
            <span className="badge bg-warning text-dark p-2">
              <i className="fas fa-hourglass-half me-1" aria-hidden="true" />
              Response Deadline: 15m
            </span>
          </div>
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="broadcasts-load-error">
          {loadError}
        </div>
      )}
      {offerMsg && (
        <div className={`alert alert-${offerMsg.type}`} role="alert" data-testid="offer-message">
          {offerMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <div className="row">
          {/* LEFT: dispatcher broadcasts */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-bullhorn me-2" aria-hidden="true" />
                  Operator Capacity Broadcasts
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Broadcast</th>
                        <th>Job Details</th>
                        <th>Targets</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {broadcasts.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            No broadcasts yet.
                          </td>
                        </tr>
                      )}
                      {broadcasts.map((b) => (
                        <tr key={b.id} data-testid="broadcast-row" data-ref={b.broadcastRef}>
                          <td className="fw-bold">{b.broadcastRef}</td>
                          <td className="small">
                            {b.bookingRef} · {b.vesselName || '—'}
                          </td>
                          <td>{b.targetCount}</td>
                          <td>
                            <span className={`badge ${statusBadge(b.status)}`} data-testid="broadcast-status">
                              {b.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: my subcontracting offers (affiliate) */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100" data-testid="offers-section">
              <div className="card-header bg-white pt-4 border-bottom-0">
                <h5 className="mb-0 text-secondary">
                  <i className="fas fa-hands me-2" aria-hidden="true" />
                  My Subcontracting Offers
                </h5>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light text-secondary small text-uppercase">
                      <tr>
                        <th>Offer</th>
                        <th>Vessel / Movement</th>
                        <th>Pre-agreed Rate</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offers.length === 0 && (
                        <tr>
                          <td colSpan={4} className="text-center text-muted py-4">
                            No open subcontracting offers.
                          </td>
                        </tr>
                      )}
                      {offers.map((o) => (
                        <tr key={o.targetId} data-testid="offer-row" data-ref={o.bookingRef}>
                          <td className="fw-bold">{o.broadcastRef}</td>
                          <td className="small">
                            {o.vesselName || '—'} (GT {o.vesselGt ?? '—'})
                            <span className="d-block text-muted">
                              {o.movementType} · {o.portName || '—'} · {fmt(o.scheduledAt)}
                            </span>
                          </td>
                          <td>
                            <span data-testid="offer-rate">${o.preAgreedHourlyRate ?? '—'}</span>/hr
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-sm btn-success me-1"
                              onClick={() => respond(o, true)}
                              disabled={busyId === o.targetId}
                              data-testid="accept-offer"
                            >
                              {busyId === o.targetId && (
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                              )}
                              Accept
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => respond(o, false)}
                              disabled={busyId === o.targetId}
                              data-testid="decline-offer"
                            >
                              Decline
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* My Subcontracted Jobs (tracking) */}
      {!loading && (
        <div className="card border-0 shadow-sm" data-testid="my-jobs-section">
          <div className="card-header bg-white pt-4 border-bottom-0">
            <h5 className="mb-0 text-secondary">
              <i className="fas fa-route me-2" aria-hidden="true" />
              My Subcontracted Jobs
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light text-secondary small text-uppercase">
                  <tr>
                    <th>Job</th>
                    <th>Vessel / Movement</th>
                    <th>Milestones</th>
                    <th>Status</th>
                    <th className="text-end">SoF</th>
                  </tr>
                </thead>
                <tbody>
                  {myJobs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-muted py-4">
                        No subcontracted jobs yet.
                      </td>
                    </tr>
                  )}
                  {myJobs.map((j) => (
                    <tr key={j.bookingRef} data-testid="job-row" data-ref={j.bookingRef}>
                      <td className="fw-bold">
                        {j.bookingRef}
                        <span className="d-block text-muted small">{j.broadcastRef}</span>
                      </td>
                      <td className="small">
                        {j.vesselName || '—'}
                        <span className="d-block text-muted">{j.movementType}</span>
                      </td>
                      <td data-testid="job-milestones">
                        {j.milestones.length === 0 ? (
                          <span className="text-muted small">No milestones yet</span>
                        ) : (
                          j.milestones.map((m) => (
                            <span key={m.type} className="badge bg-light text-dark border me-1 mb-1">
                              {m.label}
                            </span>
                          ))
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${j.status === 'Completed' ? 'bg-success' : 'bg-info text-dark'}`}
                          data-testid="job-status"
                        >
                          {j.status}
                        </span>
                      </td>
                      <td className="text-end">
                        {j.sof.submitted ? (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => setSofJob(j)}
                            data-testid="view-sof"
                          >
                            <i className="fas fa-file-signature me-1" aria-hidden="true" />
                            View SoF
                          </button>
                        ) : (
                          <span className="text-muted small">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* SoF detail modal */}
      {sofJob && (
        <>
          <div className="modal d-block" tabIndex={-1} role="dialog" data-testid="sof-modal">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-file-signature me-2 text-primary" aria-hidden="true" />
                    Statement of Facts — {sofJob.bookingRef}
                  </h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setSofJob(null)} />
                </div>
                <div className="modal-body">
                  <p className="small mb-2">
                    Vessel: <strong>{sofJob.vesselName || '—'}</strong> · {sofJob.movementType}
                  </p>
                  <h6 className="small text-uppercase text-muted">Time ladder</h6>
                  <ul className="list-group list-group-flush small mb-3">
                    {sofJob.milestones.map((m) => (
                      <li key={m.type} className="list-group-item d-flex justify-content-between" data-testid="sof-milestone">
                        <span>{m.label}</span>
                        <span className="fw-bold">{new Date(m.loggedAt).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="alert alert-success mb-0">
                    <i className="fas fa-signature me-1" aria-hidden="true" />
                    Captain e-signed by <strong data-testid="sof-signed-by">{sofJob.sof.signedBy}</strong>
                    {sofJob.sof.charge != null ? ` · charge ${sofJob.sof.charge}` : ''}.
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setSofJob(null)}>
                    Close
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => window.print()}>
                    <i className="fas fa-print me-1" aria-hidden="true" />
                    Print
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </div>
  );
}
